/**
 * AVATAR CROP DIALOG (TS-33) — Editor di posizionamento dell'immagine profilo,
 * sul modello del cambio avatar di Liferay: l'immagine si trascina (pan) e si
 * ingrandisce (zoom) dentro un riquadro la cui MASCHERA è circolare (foto profilo)
 * o quadrata (logo azienda) via prop `shape`; al salvataggio si esporta comunque
 * il ritaglio quadrato visibile come data URL (JPEG).
 *
 * Custom (niente dipendenze): viewport a dimensione fissa con overflow nascosto,
 * immagine in absolute trasformata via left/top/width/height, export su canvas.
 */

import { useState, useRef, useEffect, useCallback, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import CloseIcon from '@mui/icons-material/Close';

const VIEW = 280; // lato del riquadro di anteprima (px)
const OUT = 256; // lato dell'immagine esportata (px)
const MAX_ZOOM = 3;

interface AvatarCropDialogProps {
  /** Data URL dell'immagine da ritagliare (file caricato localmente). */
  src: string;
  onCancel: () => void;
  /** Riceve il data URL (JPEG) del ritaglio quadrato. */
  onConfirm: (dataUrl: string) => void;
  /**
   * Forma della MASCHERA di ritaglio: 'circular' (foto profilo) o 'square'
   * (logo azienda). Il ritaglio esportato è comunque quadrato in entrambi i casi;
   * cambia solo l'anteprima, così la maschera rispecchia come l'immagine sarà resa.
   */
  shape?: 'circular' | 'square';
}

export function AvatarCropDialog({ src, onCancel, onConfirm, shape = 'circular' }: AvatarCropDialogProps) {
  const { t } = useTranslation();
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ startX: number; startY: number; ox: number; oy: number } | null>(null);

  const baseScale = dims ? Math.max(VIEW / dims.w, VIEW / dims.h) : 1;
  const scale = baseScale * zoom;
  const dispW = dims ? dims.w * scale : 0;
  const dispH = dims ? dims.h * scale : 0;

  // Vincola l'offset così che l'immagine copra sempre il riquadro (niente bordi vuoti).
  const clamp = useCallback((x: number, y: number, dW: number, dH: number) => ({
    x: Math.min(0, Math.max(VIEW - dW, x)),
    y: Math.min(0, Math.max(VIEW - dH, y)),
  }), []);

  // Carica l'immagine: dimensioni naturali + centratura iniziale a zoom 1.
  // Guard `cancelled`: se la modale si chiude (o src cambia) mentre l'immagine
  // sta caricando, evitiamo setState su un componente smontato.
  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      imgRef.current = img;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const bs = Math.max(VIEW / w, VIEW / h);
      setDims({ w, h });
      setZoom(1);
      setOffset({ x: (VIEW - w * bs) / 2, y: (VIEW - h * bs) / 2 });
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [src]);

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startX: e.clientX, startY: e.clientY, ox: offset.x, oy: offset.y };
  }
  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!drag.current) return;
    const nx = drag.current.ox + (e.clientX - drag.current.startX);
    const ny = drag.current.oy + (e.clientY - drag.current.startY);
    setOffset(clamp(nx, ny, dispW, dispH));
  }
  function onPointerUp() {
    drag.current = null;
  }

  // Zoom ancorato al centro del riquadro (il punto immagine al centro resta fermo).
  function applyZoom(newZoom: number) {
    if (!dims) return;
    const z = Math.min(MAX_ZOOM, Math.max(1, newZoom));
    const oldScale = baseScale * zoom;
    const newScale = baseScale * z;
    const c = VIEW / 2;
    const imgX = (c - offset.x) / oldScale;
    const imgY = (c - offset.y) / oldScale;
    setZoom(z);
    setOffset(clamp(c - imgX * newScale, c - imgY * newScale, dims.w * newScale, dims.h * newScale));
  }

  function onWheel(e: ReactWheelEvent<HTMLDivElement>) {
    e.preventDefault();
    applyZoom(zoom - e.deltaY * 0.0015);
  }

  function handleConfirm() {
    const img = imgRef.current;
    if (!img) return;
    const k = OUT / VIEW;
    const canvas = document.createElement('canvas');
    canvas.width = OUT;
    canvas.height = OUT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, OUT, OUT);
    ctx.drawImage(img, offset.x * k, offset.y * k, dispW * k, dispH * k);
    onConfirm(canvas.toDataURL('image/jpeg', 0.9));
  }

  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth className="app-dialog">
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">{t('userSettings.profile.cropTitle')}</span>
        <IconButton
          aria-label={t('common.cancel')}
          onClick={onCancel}
          size="small"
          className="app-dialog__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <div className="avatar-crop">
          <div
            className={`avatar-crop__viewport${shape === 'square' ? ' avatar-crop__viewport--square' : ''}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={onWheel}
          >
            {dims && (
              <img
                src={src}
                alt=""
                draggable={false}
                className="avatar-crop__img"
                style={{ left: offset.x, top: offset.y, width: dispW, height: dispH }}
              />
            )}
          </div>
          <div className="avatar-crop__controls">
            <span className="text-sm text-muted">{t('userSettings.profile.zoom')}</span>
            <Slider
              min={1}
              max={MAX_ZOOM}
              step={0.01}
              value={zoom}
              onChange={(_, v) => applyZoom(v as number)}
              aria-label={t('userSettings.profile.zoom')}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions className="app-dialog__footer">
        <Button variant="contained" onClick={handleConfirm}>{t('common.save')}</Button>
        <Button variant="outlined" onClick={onCancel}>{t('common.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
}
